// 人員與存取 › 對外（SPEC #org-tree-ui 第 3 點、o10 / o14 / o15 / o24 / o28；VMS UX 評審 2026-10-13 P0-9）：
//   委派出去、委派給本組織、分享給本組織三段同一頁（第三段由呼叫端以 extra 傳入），每段一句話說差別。
//   委派出去：受託組織（審核徽章）、節點、範圍、期限、狀態標籤（待核可 / 待覆核 / 即將到期）、「N 人 ›」、〔仍需要〕〔詳情〕；〔用對方的委派代碼新增〕
//             待核可的變更（高風險委派、擴大高風險委派涵蓋的移動）：owner 或另一位 org_admin〔核可〕〔拒絕〕（申請人自己不能）
//   委派給本組織：委派方（委派人）、節點、範圍、期限、「N 人 ›」、範圍已擴大〔套用…〕、委派方要求兩步驟驗證的提示、〔指派成員〕〔詳情〕
//             〔產生委派代碼〕→ 代碼 / QR / 連結（7 天）+ 本組織代號；代碼清單的「對方處理中」
//   只限 team 組織；只有 owner / org_admin 看得到這一頁（受託方的一般成員從即時頁的樹看委派來的設備）。
import { useState, type ReactNode } from 'react';
import { Link, Route, Routes, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageAnchors } from '../../components/PageAnchors';
import { useQuery } from '@tanstack/react-query';
import { Dialog } from '../../components/Dialog';
import type { MyOrg } from '../../lib/planeApi';
import { treeApi, type Delegation, type DelegationRequest } from '../../lib/orgTreeApi';
import { actionWords } from '../../lib/orgTreeUi';
import { PairQrCode } from '../devices/PairQrCode';
import { AssignDialog } from './AssignDialog';
import { ApplyDialog, DelegationDetail } from './DelegationDetail';
import { DelegationWizard } from './DelegationWizard';
import { useTreeRefresh } from './hooks';
import { otErr } from './otErr';
import { dateStamp, stamp } from '../../lib/timefmt';

export function DelegationsTab({ org, extra, delegations = true }: { org: MyOrg; extra?: ReactNode; delegations?: boolean }) {
  return (
    <Routes>
      {delegations && <Route path=":id" element={<DelegationDetail org={org} />} />}
      <Route path="*" element={delegations ? <Lists org={org} extra={extra} /> : <>{extra}</>} />
    </Routes>
  );
}

function termOf(t: (k: string, o?: Record<string, unknown>) => string, d: Delegation, _lang: string): string {
  return d.expires_at ? t('ot.until', { date: dateStamp(d.expires_at) }) : t('ot.dlg_forever_short');
}

function nodesOf(t: (k: string, o?: Record<string, unknown>) => string, d: Delegation): string {
  return d.nodes.map((n) => (n.is_root ? t('ot.whole_org') : n.name)).join('、');
}

function Lists({ org, extra }: { org: MyOrg; extra?: ReactNode }) {
  const { t, i18n } = useTranslation();
  const out = useQuery({ queryKey: ['ot', org.org_id, 'dlg', 'out'], queryFn: () => treeApi.delegations(org, 'out'), retry: false });
  const inn = useQuery({ queryKey: ['ot', org.org_id, 'dlg', 'in'], queryFn: () => treeApi.delegations(org, 'in'), retry: false });
  const refresh = useTreeRefresh(org);
  const nav = useNavigate();
  const [wizard, setWizard] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);
  const [assignFor, setAssignFor] = useState<Delegation | null>(null);
  const [applyFor, setApplyFor] = useState<Delegation | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function review(d: Delegation) {
    try {
      await treeApi.reviewDelegation(org, d);
      setNotice(t('ot.reviewed', { org: d.to_org_name }));
      refresh();
    } catch (e) {
      setErr(otErr(t, e));
    }
  }

  return (
    <section data-testid="delegations">
      {/* 三段同一頁、最後一段要捲：頁頂段落錨點（第三輪 R3-P2-3） */}
      <PageAnchors
        items={[
          { id: 'ext-out', label: t('ot.dlg_out_of', { org: org.display_name }) },
          { id: 'ext-in', label: t('ot.dlg_in_of', { org: org.display_name }) },
          ...(extra ? [{ id: 'ext-shares', label: t('iam.ext_shares_title') }] : []),
        ]}
      />
      {notice && (
        <p className="notice card" role="status" style={{ marginTop: 12 }}>
          {notice}
        </p>
      )}
      {err && <p className="error">{err}</p>}
      <div className="card anchor-target" style={{ marginTop: 12 }} data-testid="dlg-out" id="ext-out">
          <h2 className="section-title" style={{ marginTop: 0 }}>
            {t('ot.dlg_out_of', { org: org.display_name })}
          </h2>
          <p className="muted small">{t('iam.ext_out_desc')}</p>
          {out.isLoading && <p className="muted">{t('common.loading')}</p>}
          {out.isError && <p className="error">{otErr(t, out.error)}</p>}
          {out.data && out.data.length === 0 && <p className="muted">{t('ot.no_dlg_out')}</p>}
          {out.data && out.data.length > 0 && (
            <div className="table-wrap">
              <table className="grid ot-cards">
                <thead>
                  <tr>
                    <th>{t('ot.col_to_org')}</th>
                    <th>{t('ot.wz_nodes')}</th>
                    <th>{t('ot.dlg_scope')}</th>
                    <th>{t('ot.term')}</th>
                    <th>{t('ot.col_grantees')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {out.data.map((d) => (
                    <tr key={d.delegation_id} data-dlg={d.to_org_name}>
                      <td data-label={t('ot.col_to_org')}>
                        {d.to_org_name}
                        {(d.to_org_verified_kinds?.length ?? 0) > 0 && <span className="badge ot-verified">{t('ot.verified', { kinds: d.to_org_verified_kinds!.join('、') })}</span>}
                      </td>
                      <td data-label={t('ot.wz_nodes')}>{nodesOf(t, d)}</td>
                      <td data-label={t('ot.dlg_scope')}>{actionWords(t, d.scopes)}</td>
                      <td data-label={t('ot.term')}>
                        {termOf(t, d, i18n.language)} <StatusTags d={d} side="out" />
                      </td>
                      <td data-label={t('ot.col_grantees')}>
                        <Link to={d.delegation_id}>{t('ot.n_people_link', { n: d.grantee_count ?? 0 })}</Link>
                      </td>
                      <td>
                        <div className="row">
                          {d.review_due && (
                            <button className="btn btn-sm" onClick={() => void review(d)}>
                              {t('ot.still_needed')}
                            </button>
                          )}
                          <Link className="btn btn-sm" to={d.delegation_id}>
                            {t('ot.details')}
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => setWizard(true)} data-testid="dlg-new">
            {t('ot.dlg_new_by_code')}
          </button>
        </div>
        <div className="card anchor-target" style={{ marginTop: 12 }} data-testid="dlg-in" id="ext-in">
          <h2 className="section-title" style={{ marginTop: 0 }}>
            {t('ot.dlg_in_of', { org: org.display_name })}
          </h2>
          <p className="muted small">{t('iam.ext_in_desc')}</p>
          {inn.isLoading && <p className="muted">{t('common.loading')}</p>}
          {inn.isError && <p className="error">{otErr(t, inn.error)}</p>}
          {inn.data && inn.data.length === 0 && <p className="muted">{t('ot.no_dlg_in')}</p>}
          {inn.data && inn.data.length > 0 && (
            <div className="table-wrap">
              <table className="grid ot-cards">
                <thead>
                  <tr>
                    <th>{t('ot.col_from_org')}</th>
                    <th>{t('ot.wz_nodes')}</th>
                    <th>{t('ot.dlg_scope')}</th>
                    <th>{t('ot.term')}</th>
                    <th>{t('ot.col_assigned')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {inn.data.map((d) => (
                    <tr key={d.delegation_id} data-dlg={d.from_org_name}>
                      <td data-label={t('ot.col_from_org')}>
                        {d.from_org_name}
                        {d.granted_by_name && <span className="muted small"> {t('ot.delegator_paren', { name: d.granted_by_name })}</span>}
                        {d.from_mfa_required && <div className="small ot-mfa">🔒 {t('ot.dlg_mfa_note', { from: d.from_org_name })}</div>}
                      </td>
                      <td data-label={t('ot.wz_nodes')}>{nodesOf(t, d)}</td>
                      <td data-label={t('ot.dlg_scope')}>{actionWords(t, d.scopes)}</td>
                      <td data-label={t('ot.term')}>
                        {termOf(t, d, i18n.language)} <StatusTags d={d} side="in" />
                      </td>
                      <td data-label={t('ot.col_assigned')}>
                        <Link to={d.delegation_id}>{t('ot.n_people_link', { n: d.grantee_count ?? 0 })}</Link>
                        {(d.expandable_count ?? 0) > 0 && (
                          <div className="small">
                            {t('ot.expandable', { n: d.expandable_count })}{' '}
                            <button className="link-btn" onClick={() => setApplyFor(d)}>
                              {t('ot.apply_ellipsis')}
                            </button>
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="row">
                          <button className="btn btn-sm btn-primary" onClick={() => setAssignFor(d)}>
                            {t('ot.assign_members')}
                          </button>
                          <Link className="btn btn-sm" to={d.delegation_id}>
                            {t('ot.details')}
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <button className="btn btn-sm" style={{ marginTop: 12 }} onClick={() => setCodeOpen(true)} data-testid="dlg-code-new">
            {t('ot.gen_code')}
          </button>
          <Requests org={org} />
        </div>
      {extra}
      {wizard && <DelegationWizard org={org} close={() => setWizard(false)} done={(m) => (setNotice(m), refresh())} />}
      {codeOpen && <CodeDialog org={org} close={() => setCodeOpen(false)} />}
      <AssignDialog
        org={org}
        target={assignFor ? { kind: 'delegation', delegation: assignFor } : null}
        close={() => setAssignFor(null)}
        done={() => {
          refresh();
          if (assignFor) nav(assignFor.delegation_id);
        }}
      />
      {applyFor && <ApplyDialog org={org} d={applyFor} close={() => setApplyFor(null)} done={(m) => (setNotice(m), refresh())} />}
    </section>
  );
}

export function StatusTags({ d, side }: { d: Delegation; side: 'out' | 'in' }) {
  const { t } = useTranslation();
  return (
    <>
      {side === 'out' && d.review_due && <span className="badge badge-warn">{t('ot.tag_review')}</span>}
      {d.expiring && <span className="badge badge-warn">{t('ot.tag_expiring')}</span>}
      {d.status === 'suspended' && <span className="badge">{t('ot.tag_suspended')}</span>}
      {d.status === 'ended' && <span className="badge">{t('ot.tag_ended')}</span>}
    </>
  );
}

function Requests({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ['ot', org.org_id, 'dlg-requests'], queryFn: () => treeApi.requests(org), retry: false });
  const refresh = useTreeRefresh(org);
  const list = (q.data?.requests ?? []).filter((r) => r.status === 'pending');
  if (!list.length) return null;
  return (
    <div style={{ marginTop: 12 }} data-testid="dlg-requests">
      <h3 className="ot-h3">{t('ot.my_codes')}</h3>
      <ul className="plain-list">
        {list.map((r) => (
          <li key={r.request_id} className="row">
            <span className="mono">{r.code}</span>
            <span className="muted small">{t('ot.code_expires', { date: stamp(r.expires_at) })}</span>
            {r.status === 'pending' && (
              <button className="btn btn-sm" onClick={() => void treeApi.cancelRequest(org, r.request_id).then(refresh)}>
                {t('ot.code_cancel')}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** 〔產生委派代碼〕（o10）：代碼 / QR / 連結，7 天、用一次；本組織代號（雙方可以口頭核對）。 */
function CodeDialog({ org, close }: { org: MyOrg; close(): void }) {
  const { t } = useTranslation();
  const refresh = useTreeRefresh(org);
  const [note, setNote] = useState('');
  const [r, setR] = useState<DelegationRequest | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function gen() {
    setBusy(true);
    setErr(null);
    try {
      setR(await treeApi.createRequest(org, note.trim() || undefined));
      refresh();
    } catch (e) {
      setErr(otErr(t, e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open title={t('ot.gen_code')} onClose={close}>
      <div className="form" data-testid="dlg-code">
        {!r ? (
          <>
            <p className="muted small">{t('ot.gen_code_hint')}</p>
            <label className="field">
              <span>
                {t('ot.code_note')} {t('ot.optional')}
              </span>
              <input className="input" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
            </label>
          </>
        ) : (
          <>
            <div className="pair-code mono" aria-label={t('ot.code')}>
              {r.code}
            </div>
            {r.link && <PairQrCode text={r.link} label={t('ot.code_qr')} />}
            {r.link && (
              <p className="small mono" style={{ wordBreak: 'break-all' }}>
                {r.link}
              </p>
            )}
            <p className="small">{t('ot.code_valid', { date: stamp(r.expires_at) })}</p>
            {r.org_code && <p className="small">{t('ot.org_code_line', { code: r.org_code })}</p>}
          </>
        )}
        {err && <p className="error">{err}</p>}
        <div className="dialog-actions">
          <button className="btn" onClick={close}>
            {r ? t('common.done') : t('common.cancel')}
          </button>
          {!r && (
            <button className="btn btn-primary" disabled={busy} onClick={() => void gen()}>
              {t('ot.gen_code')}
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
